import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, X } from "lucide-react";
import BookButton from "./BookButton";
import { nav, site, ui } from "@/content";

const linkCls = ({ isActive }) =>
  `np-navlink relative py-2 text-[15px] font-medium transition-colors ${isActive ? "text-forest is-active" : "text-ink/75 hover:text-forest"}`;

export default function Header() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <header className="sticky top-0 z-40">
      <div className="bg-forest text-cream/90">
        <div className="container-np flex h-9 items-center justify-between gap-4 text-xs sm:text-[13px]">
          <span className="truncate">{site.noReferral}<span className="hidden sm:inline"> · Nacka Forum &amp; Göteborg</span></span>
          <a href={site.phone.href} data-testid="topbar-phone-link" className="inline-flex shrink-0 items-center gap-1.5 hover:text-amber">
            <Phone className="h-3.5 w-3.5" aria-hidden="true" /> {site.phone.display}
          </a>
        </div>
      </div>
      <div className="border-b border-line/80 bg-cream/90 backdrop-blur-md">
        <div className="container-np flex h-[72px] items-center justify-between gap-6">
          <Link to="/" data-testid="header-logo-link" className="shrink-0" aria-label={`${site.name} – startsida`}>
            <img src={site.logo} alt={site.name} width="118" height="56" className="h-11 w-auto sm:h-12" />
          </Link>
          <nav aria-label="Huvudmeny" className="hidden items-center gap-8 lg:flex">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`nav-link-${n.to.slice(1)}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <BookButton testId="header-book-button" size="sm" className="hidden sm:inline-flex" />
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-expanded={open}
              aria-controls="mobile-menu"
              aria-label={open ? ui.closeMenu : ui.menu}
              data-testid="mobile-menu-toggle"
              className="grid h-11 w-11 place-items-center rounded-full text-forest hover:bg-sand lg:hidden"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
        {open && (
          <nav id="mobile-menu" aria-label="Mobilmeny" data-testid="mobile-menu" className="np-slide-down border-t border-line bg-cream lg:hidden">
            <ul className="container-np flex flex-col py-4">
              <li>
                <NavLink to="/" end className="block py-3 font-serif text-2xl text-ink" data-testid="mobile-nav-link-start">
                  Start
                </NavLink>
              </li>
              {nav.map((n) => (
                <li key={n.to}>
                  <NavLink to={n.to} className="block py-3 font-serif text-2xl text-ink" data-testid={`mobile-nav-link-${n.to.slice(1)}`}>
                    {n.label}
                  </NavLink>
                </li>
              ))}
              <li className="pt-4">
                <BookButton testId="mobile-menu-book-button" className="w-full" />
              </li>
            </ul>
          </nav>
        )}
      </div>
    </header>
  );
}
